import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { gr } from "../tokens";

// Status pills carry urgency (never button colour). The glyph is decorative; the text says the status.
// size="sm": a tighter pill for cards and rows.
const STATUS = {
  overdue: { fg: gr.color.overdue, bg: gr.color.overdueSoft, glyph: "!" },
  due: { fg: gr.color.brandDark, bg: gr.color.brandSoft, glyph: "●" },
  ok: { fg: gr.color.ok, bg: gr.color.okSoft, glyph: "✓" },
  late: { fg: gr.color.late, bg: gr.color.lateSoft, glyph: null },
  neutral: { fg: gr.color.muted, bg: gr.color.bg, glyph: null, border: true },
};
// How long a tapped hint stays open on a phone (long enough to read a short reason).
const HINT_TOUCH_MS = 4000;

export default function GrStatusPill({
  status = "neutral",
  glyph = true,
  hint,
  size = "md",
  children,
  sx,
  ...rest
}) {
  const s = STATUS[status] || STATUS.neutral;
  const pill = (
    <Box
      component="span"
      // With a hint the pill can be focused, so keyboard users reach the hint too.
      tabIndex={hint ? 0 : undefined}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: gr.space[1],
        alignSelf: "flex-start",
        padding: size === "sm" ? `0 ${gr.space[2]}` : `2px ${gr.space[3]}`,
        borderRadius: gr.radius.pill,
        border: `1px solid ${s.border ? gr.color.line : "transparent"}`,
        background: s.bg,
        color: s.fg,
        fontFamily: gr.font.family,
        fontSize: gr.font.size.xs,
        fontWeight: gr.font.weight.semibold,
        lineHeight: 1.5,
        whiteSpace: "nowrap",
        ...(hint && {
          cursor: "help",
          outline: "none",
          "&:focus-visible": { boxShadow: gr.shadow.focus },
        }),
        ...sx,
      }}
      {...rest}
    >
      {glyph && s.glyph ? (
        <Box
          component="span"
          aria-hidden="true"
          sx={{ fontSize: "0.85em", lineHeight: 1 }}
        >
          {s.glyph}
        </Box>
      ) : null}
      {children}
    </Box>
  );
  if (!hint) return pill;
  // The hint (e.g. why a post was rejected) opens on hover, on keyboard focus and on a tap (phones
  // have no hover); screen readers hear it after the status.
  return (
    <Tooltip
      title={hint}
      describeChild
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={HINT_TOUCH_MS}
    >
      {pill}
    </Tooltip>
  );
}

GrStatusPill.propTypes = {
  status: PropTypes.oneOf(Object.keys(STATUS)),
  glyph: PropTypes.bool,
  /** Extra detail shown on hover, focus or tap (e.g. the rejection reason). */
  hint: PropTypes.node,
  size: PropTypes.oneOf(["md", "sm"]),
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};
